import { useEffect, type JSX } from "react";
import {
  createFileRoute,
  useParams,
  useRouteContext,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";
import {
  BillingQueryDocument,
  NewBillingItemDetailsPageQueryDocument,
  PixPageQueryDocument,
  UserQueryDocument,
} from "@repo/graphql-types/graphql";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL, useInvalidateQuery } from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";

import { CreateNewPix } from "@/components/payment/create-new-pix";
import { PixDetails } from "@/components/payment/details/pix-details";

const PixPageQuery = graphql(`
  query PixPageQuery($billingId: Int!) {
    cobrancasFechadas(where: { codCobranca: { eq: $billingId } }) {
      billings: nodes {
        temPixValido
        cobrancaPaga
        ...CreateNewPixFragment
        ...PixDetailsFragment
      }
    }
  }
`);

const PixPage = (): JSX.Element => {
  const { billingId } = useParams({
    from: "/settings/billing/details/$billingId/pix",
  });
  const parsedBillingId = Number(billingId);
  const { user } = useRouteContext({ from: "__root__" });
  const params: { from?: string } = useSearch({ strict: false });
  const router = useRouter();

  const queryData = useGraphQL(
    PixPageQuery,
    {
      billingId: parsedBillingId,
    },
    {
      staleTime: 3 * 60 * 1000, // 3 minutes
    },
  );

  const invalidatePixPage = useInvalidateQuery(PixPageQueryDocument);
  const invalidateBillingPage = useInvalidateQuery(BillingQueryDocument);

  const invalidateMembroPage = useInvalidateQuery(UserQueryDocument);
  const invalidateDetailsPage = useInvalidateQuery(
    NewBillingItemDetailsPageQueryDocument,
  );

  const { data } = queryData;
  const billing = data?.cobrancasFechadas?.billings?.[0];
  // biome-ignore lint/complexity/useOptionalChain: legacy code
  const hasValidPix = billing && billing.temPixValido;

  useEffect(() => {
    const interval = setInterval(() => {
      invalidatePixPage();
      invalidateBillingPage();
      invalidateDetailsPage();
      invalidateMembroPage();
    }, 30000);

    return () => clearInterval(interval);
  }, [
    invalidateBillingPage,
    invalidateDetailsPage,
    invalidateMembroPage,
    invalidatePixPage,
  ]);

  const handleGoBack = (): void => {
    if (Boolean(billing?.cobrancaPaga) || params.from === "billing-actions") {
      router.history.go(-2);
      return;
    }

    router.history.back();
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" onClick={handleGoBack} />
        <HeaderTitle title="Pix" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryData}>
          {billing ? (
            // biome-ignore lint/complexity/noUselessFragments: legacy code
            <>
              {!hasValidPix && !user.pj && !billing.cobrancaPaga ? (
                <CreateNewPix data={billing} />
              ) : (
                <PixDetails data={billing} />
              )}
            </>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/billing/details/$billingId/pix")({
  component: PixPage,
});
